/**
 * The numbers a company is allowed to disagree with.
 *
 * `06` §Configuration: *"Every threshold above is a company-level setting with
 * a documented default, overridable per project by the PM. The settings screen
 * shows the default beside the current value, so a company can see how far it
 * has drifted from the recommendation."*
 *
 * That last sentence is the whole design. These are not preferences — they
 * decide when a project goes amber and when somebody gets chased, and a company
 * that has quietly moved "late" from 7 days to 30 has not tuned the product,
 * it has switched it off. So every field carries its default, the screen prints
 * it beside whatever the company chose, and drifting is visible rather than
 * merely possible.
 *
 * WHY THE CATALOGUE IS SHARED
 * ---------------------------
 * The server validates against it and the screen renders from it. One list, so
 * a field cannot be added to the form and silently rejected by the API, and a
 * bound cannot be enforced on one side and not the other.
 *
 * PER-PROJECT OVERRIDES ARE NOT BUILT. `06` allows them; nobody has asked, and
 * a second layer of thresholds is a second place to look when a project's
 * colour is not what somebody expected. Company-level first.
 */

/** Every tunable number, with the value the product ships with. */
export const THRESHOLD_DEFAULTS = {
  // --- approvals
  approval_warn_days: 2,
  approval_red_days: 7,
  approval_escalation_days: 3,
  // --- schedule
  red_slip_days: 7,
  // --- procurement
  procurement_buffer_days: 5,
  // --- site
  diary_gap_amber: 2,
  diary_gap_red: 3,
  // --- quality
  repeat_ncr_count: 3,
  repeat_ncr_days: 30,
  snag_age_critical: 2,
  snag_age_high: 5,
  snag_age_medium: 14,
  snag_age_low: 30,
} as const;

export type ThresholdKey = keyof typeof THRESHOLD_DEFAULTS;
export type Thresholds = Record<ThresholdKey, number>;

export interface ThresholdField {
  readonly key: ThresholdKey;
  /** The heading a person reads. Never the key. */
  readonly label: string;
  /** What moving it actually changes, in the language of the job. */
  readonly help: string;
  readonly group: 'Approvals' | 'Programme' | 'Materials' | 'Site' | 'Quality';
  readonly min: number;
  readonly max: number;
}

/**
 * The catalogue, in the order the screen shows it.
 *
 * `min` and `max` are not cosmetic. Zero on an age threshold would make every
 * snag overdue the moment it is raised; a thousand would mean nothing is ever
 * late. Both are ways of turning the signal off while appearing to keep it, so
 * the bounds keep a company inside "disagrees with us" and out of "has silently
 * disabled it".
 */
export const THRESHOLD_FIELDS: readonly ThresholdField[] = [
  {
    key: 'approval_warn_days',
    label: 'Warn this many days before an approval is due',
    help: 'A submittal still inside its date, but close enough to chase.',
    group: 'Approvals',
    min: 0,
    max: 30,
  },
  {
    key: 'approval_red_days',
    label: 'An approval is critical after this many days overdue',
    help: 'Turns the Approvals light red and puts it on the project health.',
    group: 'Approvals',
    min: 1,
    max: 60,
  },
  {
    key: 'approval_escalation_days',
    label: 'Chase the project manager after this many days overdue',
    help: 'The daily chase tells the PM. Raising it means nobody is told for longer.',
    group: 'Approvals',
    min: 1,
    max: 60,
  },
  {
    key: 'red_slip_days',
    label: 'A critical milestone is red once it slips this many days',
    help: 'Below this it is amber. This is the number the programme is judged on.',
    group: 'Programme',
    min: 1,
    max: 90,
  },
  {
    key: 'procurement_buffer_days',
    label: 'Treat a material as at risk if it lands within this many days',
    help: 'The cushion between arriving and being needed. Bigger means earlier warning.',
    group: 'Materials',
    min: 0,
    max: 60,
  },
  {
    key: 'diary_gap_amber',
    label: 'Warn after this many working days with no site diary',
    help: 'A silent diary is how a project looks fine while nobody is writing anything down.',
    group: 'Site',
    min: 1,
    max: 30,
  },
  {
    key: 'diary_gap_red',
    label: 'Site goes red after this many working days with no diary',
    help: 'Must be at least the warning above, or the warning never shows.',
    group: 'Site',
    min: 1,
    max: 60,
  },
  {
    key: 'repeat_ncr_count',
    label: 'This many NCRs against one trade is a pattern',
    help: 'Counted over the window below. It names the trade rather than the record.',
    group: 'Quality',
    min: 2,
    max: 50,
  },
  {
    key: 'repeat_ncr_days',
    label: 'Look back this many days for a repeat',
    help: 'A longer window catches slower patterns and forgives less.',
    group: 'Quality',
    min: 7,
    max: 365,
  },
  {
    key: 'snag_age_critical',
    label: 'A critical snag is overdue after',
    help: 'Days from being raised. Critical is the one somebody should already be walking to.',
    group: 'Quality',
    min: 1,
    max: 90,
  },
  {
    key: 'snag_age_high',
    label: 'A high snag is overdue after',
    help: 'Days from being raised.',
    group: 'Quality',
    min: 1,
    max: 180,
  },
  {
    key: 'snag_age_medium',
    label: 'A medium snag is overdue after',
    help: 'Days from being raised.',
    group: 'Quality',
    min: 1,
    max: 365,
  },
  {
    key: 'snag_age_low',
    label: 'A low snag is overdue after',
    help: 'Days from being raised.',
    group: 'Quality',
    min: 1,
    max: 365,
  },
];

export type ThresholdFailure =
  | { readonly kind: 'unknown_key'; readonly key: string }
  | { readonly kind: 'not_a_whole_number'; readonly key: ThresholdKey }
  | { readonly kind: 'out_of_range'; readonly key: ThresholdKey; readonly min: number; readonly max: number }
  | { readonly kind: 'red_before_amber' };

const FIELD_BY_KEY = new Map(THRESHOLD_FIELDS.map((f) => [f.key, f]));

/**
 * Check a proposed set. Returns every problem, not the first.
 *
 * Somebody adjusting a whole screenful should be told everything that is wrong
 * with it in one go rather than submitting five times.
 */
export function checkThresholds(proposed: Partial<Record<string, unknown>>): readonly ThresholdFailure[] {
  const failures: ThresholdFailure[] = [];

  for (const [key, value] of Object.entries(proposed)) {
    const field = FIELD_BY_KEY.get(key as ThresholdKey);
    if (!field) {
      failures.push({ kind: 'unknown_key', key });
      continue;
    }
    if (typeof value !== 'number' || !Number.isInteger(value)) {
      failures.push({ kind: 'not_a_whole_number', key: field.key });
      continue;
    }
    if (value < field.min || value > field.max) {
      failures.push({ kind: 'out_of_range', key: field.key, min: field.min, max: field.max });
    }
  }

  /*
   * The one relationship between two fields.
   *
   * Red before amber is not out of range on either field, and it silently
   * removes the warning: the diary would go straight from fine to red and the
   * amber state — the one that gives somebody a day to fix it — would never
   * appear. Checked here rather than left to the screen, because a rule the UI
   * enforces is a rule the API does not.
   */
  const amber = proposed['diary_gap_amber'];
  const red = proposed['diary_gap_red'];
  if (typeof amber === 'number' && typeof red === 'number' && red < amber) {
    failures.push({ kind: 'red_before_amber' });
  }

  return failures;
}

/** A company's stored overrides, merged onto the defaults. Unknown keys ignored. */
export function mergeThresholds(stored: unknown): Thresholds {
  const merged: Thresholds = { ...THRESHOLD_DEFAULTS };
  if (!stored || typeof stored !== 'object') return merged;

  for (const [key, value] of Object.entries(stored as Record<string, unknown>)) {
    const field = FIELD_BY_KEY.get(key as ThresholdKey);
    // A stored value outside its bounds is IGNORED rather than clamped: it can
    // only come from a bound that was tightened after it was saved, and quietly
    // using a number nobody would be allowed to choose today is worse than
    // falling back to the documented default.
    if (!field || typeof value !== 'number' || !Number.isInteger(value)) continue;
    if (value < field.min || value > field.max) continue;
    merged[field.key] = value;
  }
  return merged;
}

/** Which of them this company has actually moved. Drives "changed from the default". */
export function driftFromDefaults(thresholds: Thresholds): readonly ThresholdKey[] {
  return THRESHOLD_FIELDS.filter((f) => thresholds[f.key] !== THRESHOLD_DEFAULTS[f.key]).map(
    (f) => f.key,
  );
}
